import { useEffect, useRef, useState } from 'react';
import ServiceIcon from './ServiceIcon';
import { useInquiryModal } from '@/components/utilities/InquiryModalContext';

const CheckIcon = () => (
  <svg className="mt-px shrink-0" width={18} height={18} viewBox="0 0 18 18" fill="none" aria-hidden="true">
    <circle cx="9" cy="9" r="9" fill="#E6F9F1" />
    <path
      d="M5 9.5L7.5 12L13 6.5"
      stroke="#07c276"
      strokeWidth="1.7"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const PriceBadge = ({ label }) => (
  <span className="inline-flex items-center rounded-full bg-[#E6F9F1] px-5 py-2.5 text-sm font-semibold whitespace-nowrap text-[#056044]">
    {label}
  </span>
);

const CtaButton = ({ label, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    className="inline-flex cursor-pointer items-center rounded-full bg-brand-400 px-5 py-2.5 text-sm font-normal text-white transition-colors hover:bg-brand-500"
  >
    {label}
  </button>
);

export default function ServicesAccordionBlock({
  eyebrow,
  servicesAccordionTitle: title,
  servicesAccordionDescription: description,
  servicesAccordionItems: items = []
}) {
  const [openIndex, setOpenIndex] = useState(null);

  return (
    <section className="bg-white pt-[72px] pb-20 max-md:py-12">
      <div className="mx-auto w-full max-w-[1224px] px-6">
        {(eyebrow || title || description) && (
          <div className="mb-12">
            {eyebrow && (
              <span className="mb-4 inline-block text-sm font-semibold tracking-[0.08em] text-[#5d5d5d] uppercase">
                {eyebrow}
              </span>
            )}
            {title && <h2 className="mb-3 text-[48px] leading-[1.2] text-[#1D1816] max-md:text-[28px]">{title}</h2>}
            {description && (
              <p className="max-w-[560px] text-lg leading-[1.6] text-[#6D6D6D]">{description}</p>
            )}
          </div>
        )}

        <div className="flex flex-col overflow-hidden rounded-2xl border border-[#e5e5e5] bg-white">
          {items.map((item, index) => (
            <AccordionItem
              key={item.id}
              item={item}
              isOpen={openIndex === index}
              onToggle={() => setOpenIndex(openIndex === index ? null : index)}
            />
          ))}
        </div>
      </div>
    </section>
  );
}

function AccordionItem({ item, isOpen, onToggle }) {
  const { open } = useInquiryModal();
  const bodyRef = useRef(null);
  const [height, setHeight] = useState(0);

  useEffect(() => {
    if (isOpen && bodyRef.current) {
      setHeight(bodyRef.current.scrollHeight);
    } else {
      setHeight(0);
    }
  }, [isOpen]);

  const onCta = () => open(item.serviceId);

  return (
    <div className="border-b border-[#e5e5e5] last:border-b-0">
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={isOpen}
        className={`font-sans flex w-full cursor-pointer items-center justify-between gap-4 border-none px-8 py-6 text-left text-base font-semibold text-[#1D1816] transition-colors duration-150 max-md:px-6 max-md:py-5 ${
          isOpen ? 'bg-[#f5f5f5]' : 'bg-transparent hover:bg-[#f5f5f5]'
        }`}
      >
        <span className="flex items-center gap-3.5">
          <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-brand-400 bg-transparent">
            <ServiceIcon name={item.icon} size={20} />
          </span>
          <span>{item.title}</span>
        </span>
        <svg
          width="20"
          height="20"
          viewBox="0 0 20 20"
          fill="none"
          aria-hidden="true"
          className={`shrink-0 text-[#6D6D6D] transition-transform duration-[250ms] ease-[cubic-bezier(0.4,0,0.2,1)] ${
            isOpen ? 'rotate-180' : ''
          }`}
        >
          <path
            d="M5 7.5L10 12.5L15 7.5"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </button>

      <div
        className="overflow-hidden transition-[max-height] duration-[350ms] ease-[cubic-bezier(0.4,0,0.2,1)]"
        style={{ maxHeight: height }}
      >
        <div ref={bodyRef} className="pt-5 pr-8 pb-8 pl-[82px] max-md:px-6 max-md:pt-5 max-md:pb-6">
          {item.intro && (
            <p className="mb-5 text-base leading-[1.7] text-[#6D6D6D]">{item.intro}</p>
          )}

          {(item.sections || []).map((section, idx) => (
            <AccordionSection
              key={section.id}
              section={section}
              isFirst={idx === 0}
              fallbackCta={item.ctaLabel}
              onCta={onCta}
            />
          ))}

          {(item.priceLabel || item.ctaLabel) && (
            <div className="mt-6 flex flex-wrap items-center gap-3">
              {item.priceLabel && <PriceBadge label={item.priceLabel} />}
              {item.ctaLabel && <CtaButton label={item.ctaLabel} onClick={onCta} />}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function AccordionSection({ section, isFirst, fallbackCta, onCta }) {
  const hasList = section.listItems && section.listItems.length > 0;
  const hasNested = section.nestedItems && section.nestedItems.length > 0;
  const ctaLabel = section.ctaLabel || (section.priceLabel ? fallbackCta : null);

  return (
    <div className={isFirst ? '' : 'mt-6'}>
      {section.subtitle && (
        <p className="mb-2.5 text-sm font-bold tracking-[0.08em] text-[#5D5D5D] uppercase">
          {section.subtitle}
        </p>
      )}
      {section.introText && (
        <p className="mb-3 text-sm leading-[1.65] text-[#6D6D6D]">{section.introText}</p>
      )}

      {hasList && (
        <ul className="mb-2 flex flex-col gap-2">
          {section.listItems.map((li) => (
            <li
              key={li.id}
              className="flex items-start gap-2.5 text-base leading-[1.5] text-[#1D1816]"
            >
              <CheckIcon />
              <span>{li.text}</span>
            </li>
          ))}
        </ul>
      )}

      {hasNested && (
        <div className="mt-4 flex flex-col gap-4">
          {section.nestedItems.map((ni) => (
            <div key={ni.id} className="rounded-[0.625rem] bg-[#f5f5f5] px-4 py-3">
              <div className="mb-0.5 text-base font-semibold text-[#1D1816]">{ni.title}</div>
              {ni.priceLabel && (
                <div className="text-base text-[#6D6D6D]">{ni.priceLabel}</div>
              )}
            </div>
          ))}
        </div>
      )}

      {(section.priceLabel || ctaLabel) && (
        <div className="mt-6 flex flex-wrap items-center gap-3">
          {section.priceLabel && <PriceBadge label={section.priceLabel} />}
          {ctaLabel && <CtaButton label={ctaLabel} onClick={onCta} />}
        </div>
      )}
    </div>
  );
}
